"use client"

import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"

/**
 * Ad budget, two ways to back into the number:
 * - By customer goal: budget = target new customers × CAC.
 *   Default: 100 × $50 = $5,000.
 * - By revenue goal: budget = target revenue ÷ target ROAS.
 *   Default: $50,000 ÷ 4 = $12,500.
 */
export function AdBudgetWidget() {
  const [customers, setCustomers] = useState("100")
  const [cac, setCac] = useState("50")
  const [revenue, setRevenue] = useState("50000")
  const [roas, setRoas] = useState("4")

  const byCustomers = (() => {
    const n = parseNum(customers)
    const c = parseNum(cac)
    return n !== null && c !== null ? n * c : NaN
  })()

  const byRevenue = (() => {
    const r = parseNum(revenue)
    const t = parseNum(roas)
    return r !== null && t !== null && t > 0 ? r / t : NaN
  })()

  return (
    <CalculatorCard title="Ad Budget Calculator">
      <Tabs defaultValue="customers">
        <TabsList className="mb-6">
          <TabsTrigger value="customers">By customer goal</TabsTrigger>
          <TabsTrigger value="revenue">By revenue goal</TabsTrigger>
        </TabsList>

        <TabsContent value="customers">
          <CalculatorGrid>
            <NumberField
              id="budget-customers"
              label="Target new customers"
              value={customers}
              onChange={setCustomers}
              min={0}
              step={1}
            />
            <NumberField
              id="budget-cac"
              label="Expected CAC"
              value={cac}
              onChange={setCac}
              prefix="$"
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Required ad budget"
              value={formatCurrency(byCustomers)}
              caption={
                Number.isFinite(byCustomers)
                  ? `To acquire that many customers at this CAC, plan for ${formatCurrency(byCustomers)} in spend.`
                  : "Enter a customer goal and CAC."
              }
            />
          </div>
          <FormulaNote>Budget = Target new customers × Expected CAC.</FormulaNote>
        </TabsContent>

        <TabsContent value="revenue">
          <CalculatorGrid>
            <NumberField
              id="budget-revenue"
              label="Target revenue from ads"
              value={revenue}
              onChange={setRevenue}
              prefix="$"
            />
            <NumberField
              id="budget-roas"
              label="Target ROAS"
              value={roas}
              onChange={setRoas}
              suffix="×"
            />
          </CalculatorGrid>
          <div className="mt-6">
            <ResultStat
              label="Required ad budget"
              value={formatCurrency(byRevenue)}
              caption={
                Number.isFinite(byRevenue)
                  ? `To hit that revenue at this ROAS, plan for ${formatCurrency(byRevenue)} in spend.`
                  : "Enter a revenue goal and a non-zero target ROAS."
              }
            />
          </div>
          <FormulaNote>Budget = Target revenue ÷ Target ROAS.</FormulaNote>
        </TabsContent>
      </Tabs>
    </CalculatorCard>
  )
}
